import { Controller } from 'stimulus';
import { loadStripe } from '@stripe/stripe-js';
import Appsignal from 'utils/appsignal';
import { csrfFetch } from 'utils/fetch';

const POLL_INTERVAL_MS = 2000;
const POLL_LIMIT_MS = 60000;
const DONE_STATES = ['completed', 'disputed'];
const FAILED_STATES = ['failed', 'abandoned', 'refunded', 'revoked', 'dispute_lost'];

// The one-time purchase checkout's payment step. Every amount comes from the server: the quote endpoint prices the
// code, the purchase endpoint prices the charge, and Pay refuses to confirm when the two disagree with the total shown.
export default class extends Controller {
  static targets = [
    'payStep', 'processing', 'slow', 'success',
    'listPriceRow', 'listPrice', 'discountRow', 'discountCode', 'discountLabel', 'discount', 'total',
    'codeToggle', 'codeForm', 'codeInput', 'codeError', 'applyButton', 'removeCode',
    'walletSection', 'wallet', 'card', 'payMessage', 'payButton', 'payLabel', 'spinner',
    'recipient', 'fromName', 'note', 'sentTo'
  ];

  static values = {
    account: String,
    publicKey: String,
    country: { type: String, default: 'US' },
    currency: String,
    amountCents: Number,
    productName: String,
    quoteUrl: String,
    purchaseUrl: String,
    confirmUrl: String,
    statusUrl: String,
    coupon: { type: String, default: '' },
    gift: { type: Boolean, default: false }
  };

  connect() {
    this.busy = false;
    this.cardComplete = false;
    this.pollTimer = null;
    if (!this.giftValue) this.updateRemoveCode();
    this.setup();
  }

  async setup() {
    try {
      this.stripe = await loadStripe(this.publicKeyValue, { stripeAccount: this.accountValue });
      this.elements = this.stripe.elements();
      this.mountCard();
      await this.mountWallet();
    } catch (error) {
      Appsignal.sendError(error);
      this.showPayMessage("We couldn't load the payment form. Reload the page and try again.");
    }
  }

  disconnect() {
    clearTimeout(this.pollTimer);
    this.paymentRequest = null;
  }

  mountCard() {
    this.cardElement = this.elements.create('card');
    this.cardElement.mount(this.cardTarget);
    this.cardElement.on('change', (event) => {
      this.cardComplete = event.complete;
      this.showPayMessage(event.error ? event.error.message : '');
      this.syncPayButton();
    });
  }

  async mountWallet() {
    this.paymentRequest = this.stripe.paymentRequest({
      country: this.countryValue,
      currency: this.currencyValue,
      total: this.walletTotal(),
      requestPayerName: false,
      requestPayerEmail: false,
      disableWallets: ['link']
    });

    const available = await this.paymentRequest.canMakePayment();
    if (!available || (!available.applePay && !available.googlePay)) return;

    const button = this.elements.create('paymentRequestButton', { paymentRequest: this.paymentRequest });
    // The wallet sheet must not open for a gift with no recipient yet.
    button.on('click', (event) => {
      if (!this.recipientMissing()) return;
      event.preventDefault();
      this.recipientTarget.focus();
      this.showPayMessage("Enter the email address of the person you're gifting.");
    });
    button.mount(this.walletTarget);
    this.walletSectionTarget.classList.remove('hidden');
    this.walletSectionTarget.classList.add('flex');
    this.paymentRequest.on('paymentmethod', (event) => this.pay({ walletEvent: event }));
  }

  walletTotal() {
    return { label: this.productNameValue, amount: this.amountCentsValue };
  }

  recipientMissing() {
    return this.giftValue && !this.recipientTarget.value.trim();
  }

  syncPayButton() {
    this.payButtonTarget.disabled = this.busy || !this.cardComplete || this.recipientMissing();
  }

  // A gift is quoted with the gift promotion alone, so it never sends a code.
  quoteBody(coupon) {
    return this.giftValue ? { purpose: 'gift' } : { coupon };
  }

  purchaseBody() {
    if (!this.giftValue) return { coupon: this.couponValue };
    this.sentTo = this.recipientTarget.value.trim();
    return {
      recipient_email: this.sentTo,
      from_name: this.fromNameTarget.value.trim(),
      message: this.noteTarget.value.trim()
    };
  }

  setBusy(busy) {
    this.busy = busy;
    this.spinnerTarget.classList.toggle('hidden', !busy);
    this.syncPayButton();
  }

  showPayMessage(text) {
    this.payMessageTarget.textContent = text;
  }

  async post(url, body = {}) {
    const response = await csrfFetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    });
    const data = await response.json().catch(() => ({}));
    return { response, data };
  }

  // The promo code

  showCode() {
    this.codeToggleTarget.classList.add('hidden');
    this.codeFormTarget.classList.remove('hidden');
    this.codeInputTarget.focus();
  }

  async applyCode() {
    const code = this.codeInputTarget.value.trim();
    if (!code) return this.removeCode();

    this.applyButtonTarget.disabled = true;
    this.codeErrorTarget.textContent = '';
    try {
      const { response, data } = await this.post(this.quoteUrlValue, { coupon: code });
      if (response.ok) {
        this.couponValue = code;
        this.renderQuote(data);
        this.updateRemoveCode();
      } else {
        this.codeErrorTarget.textContent = data.error || "We couldn't check that code. Please try again.";
      }
    } catch (error) {
      Appsignal.sendError(error);
      this.codeErrorTarget.textContent = "We couldn't check that code. Please try again.";
    } finally {
      this.applyButtonTarget.disabled = false;
    }
  }

  async removeCode() {
    this.codeErrorTarget.textContent = '';
    this.codeInputTarget.value = '';
    this.couponValue = '';
    this.updateRemoveCode();
    const { response, data } = await this.post(this.quoteUrlValue, { coupon: '' });
    if (response.ok) this.renderQuote(data);
  }

  updateRemoveCode() {
    this.removeCodeTarget.classList.toggle('hidden', !this.couponValue);
  }

  // The total and the wallet amount move only with a server quote.
  renderQuote(quote) {
    this.amountCentsValue = quote.amount_cents;
    this.listPriceTarget.textContent = quote.list_price;
    this.totalTarget.textContent = quote.amount;
    this.payLabelTarget.textContent = this.giftValue ? `Pay ${quote.amount} and send gift` : `Pay ${quote.amount}`;
    if (this.giftValue) this.listPriceRowTarget.classList.toggle('hidden', !quote.promotion);
    this.discountRowTarget.classList.toggle('hidden', !quote.promotion);
    if (quote.promotion) {
      this.discountLabelTarget.textContent = quote.promotion.label;
      this.discountTarget.textContent = `-${quote.discount}`;
      const code = quote.promotion.code;
      this.discountCodeTarget.textContent = code || '';
      this.discountCodeTarget.classList.toggle('hidden', !code);
    }
    this.paymentRequest?.update({ total: this.walletTotal() });
  }

  // Paying

  async pay({ walletEvent = null } = {}) {
    if (this.busy) return walletEvent?.complete('fail');

    this.setBusy(true);
    this.showPayMessage('');
    try {
      const { data } = await this.post(this.purchaseUrlValue, this.purchaseBody());
      await this.handleCreate(data, walletEvent);
    } catch (error) {
      Appsignal.sendError(error);
      walletEvent?.complete('fail');
      this.showPayMessage("We couldn't reach the payment processor. Please try again.");
      this.setBusy(false);
    }
  }

  async handleCreate(data, walletEvent) {
    switch (data.status) {
      case 'requires_payment':
        return this.confirmPayment(data, walletEvent);
      case 'already_owned':
      case 'succeeded':
        walletEvent?.complete('success');
        return this.showSuccess();
      case 'processing':
        walletEvent?.complete('success');
        return this.startPolling(data.purchase_id);
      case 'gift_invalid':
        walletEvent?.complete('fail');
        this.recipientTarget.focus();
        return this.stopWith(data.error);
      case 'gift_from_name_invalid':
        walletEvent?.complete('fail');
        this.fromNameTarget.focus();
        return this.stopWith(data.error);
      case 'gift_message_invalid':
        walletEvent?.complete('fail');
        this.noteTarget.focus();
        return this.stopWith(data.error);
      case 'promotion_invalid':
        walletEvent?.complete('fail');
        this.showCode();
        this.codeErrorTarget.textContent = data.error;
        // The code no longer applies, so the total shown goes back to what the server would charge.
        this.couponValue = '';
        this.updateRemoveCode();
        await this.refreshQuote();
        return this.stopWith('That code no longer applies. Check the new total and pay again.');
      default:
        walletEvent?.complete('fail');
        return this.stopWith(data.error || "We couldn't start your payment. Please try again.");
    }
  }

  stopWith(message) {
    this.showPayMessage(message);
    this.setBusy(false);
  }

  async confirmPayment(data, walletEvent) {
    // The buyer approves the displayed total, so a charge at any other amount is shown to them first, never paid.
    if (data.amount_cents !== this.amountCentsValue) {
      walletEvent?.complete('fail');
      await this.refreshQuote();
      return this.stopWith('The price changed. Check the new total and pay again.');
    }

    const result = walletEvent
      ? await this.confirmWallet(data.client_secret, walletEvent)
      : await this.stripe.confirmCardPayment(data.client_secret, { payment_method: { card: this.cardElement } });

    if (result.error) {
      return this.stopWith(result.error.message);
    }
    return this.confirmOnServer(data.purchase_id);
  }

  // handleActions: false lets the wallet sheet close before any 3DS challenge opens.
  async confirmWallet(clientSecret, walletEvent) {
    const first = await this.stripe.confirmCardPayment(clientSecret, { payment_method: walletEvent.paymentMethod.id }, { handleActions: false });
    if (first.error) {
      walletEvent.complete('fail');
      return first;
    }
    walletEvent.complete('success');
    if (first.paymentIntent.status === 'requires_action') {
      return this.stripe.confirmCardPayment(clientSecret);
    }
    return first;
  }

  async refreshQuote() {
    const { response, data } = await this.post(this.quoteUrlValue, this.quoteBody(this.couponValue));
    if (response.ok) this.renderQuote(data);
  }

  async confirmOnServer(purchaseId) {
    const { data } = await this.post(this.confirmUrlValue.replace(':id', purchaseId));
    if (data.status === 'succeeded' || data.status === 'already_owned') return this.showSuccess();
    if (data.status === 'processing') return this.startPolling(purchaseId);
    return this.stopWith(data.error || "We couldn't confirm your payment. Please try again.");
  }

  // After Pay

  show(target) {
    [this.payStepTarget, this.processingTarget, this.slowTarget, this.successTarget].forEach((element) => {
      const visible = element === target;
      element.classList.toggle('hidden', !visible);
      element.classList.toggle('flex', visible);
    });
  }

  showSuccess() {
    clearTimeout(this.pollTimer);
    if (this.giftValue) this.sentToTarget.textContent = this.sentTo;
    this.show(this.successTarget);
  }

  startPolling(purchaseId) {
    this.show(this.processingTarget);
    const startedAt = Date.now();
    const poll = async () => {
      try {
        const response = await csrfFetch(this.statusUrlValue.replace(':id', purchaseId), { headers: { Accept: 'application/json' } });
        const { status } = await response.json();
        if (DONE_STATES.includes(status)) return this.showSuccess();
        if (FAILED_STATES.includes(status)) return this.paymentFailed();
      } catch (error) {
        Appsignal.sendError(error);
      }
      if (Date.now() - startedAt >= POLL_LIMIT_MS) return this.show(this.slowTarget);
      this.pollTimer = setTimeout(poll, POLL_INTERVAL_MS);
    };
    poll();
  }

  paymentFailed() {
    this.show(this.payStepTarget);
    this.stopWith("Your payment didn't go through. Try again.");
  }
}
